import type {
  MiraklAdUnit,
  MiraklListingIdentity,
  MiraklProductId,
} from '@noths/polaris-client-mirakl-tracking';
import { MIRAKL_PAGE } from '@noths/polaris-client-mirakl-tracking';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectCurrentPage } from 'src/redux/products/selectors/selectors';
import { selectPageCategories } from 'src/redux/productsQuery/selectors/selectPageCategories';
import { getCategoryIdFromCanonicalFilters } from 'src/redux/productsQuery/utils/getCategoryIdFromCanonicalFilters';
import { PageType } from 'src/types/navigation';

/** Mirakl wants the first 20 organic products of a page. */
export const MIRAKL_VIEW_PRODUCT_LIMIT = 20;

export type MiraklListingViewEvent = MiraklListingIdentity & {
  pageNumber: number;
  productIds: MiraklProductId[];
};

/**
 * What the listing page in the store is, as far as Mirakl is concerned, or null for a page Mirakl
 * has no identity for (partner pages, a category page without a category id). Shared by the view
 * event and the sponsored products ad request, so both name the page the same way.
 */
export const getMiraklListingIdentity = (
  state: ReduxApplicationState,
): MiraklListingIdentity | null => {
  const { navigation, productsQuery } = state;

  switch (navigation.pageType) {
    case PageType.Search:
      return productsQuery.searchTerm
        ? { keywords: productsQuery.searchTerm, pageId: MIRAKL_PAGE.SEARCH }
        : null;
    case PageType.Category: {
      const categoryId = getCategoryIdFromCanonicalFilters(productsQuery.canonicalFilters);

      return categoryId === null
        ? null
        : { categoryId: String(categoryId), pageId: MIRAKL_PAGE.PLP };
    }
    case PageType.Taxonomy: {
      const categoryId = selectPageCategories(state)?.[0];

      return categoryId ? { categoryId, pageId: MIRAKL_PAGE.PLP } : null;
    }
    default:
      return null;
  }
};

/**
 * The Mirakl `view` event for the listing page in the store, or null when the page has no identity
 * Mirakl knows (partner pages) or the listings request failed. A page with no results still sends
 * the view with an empty product list: Mirakl asked for the search view with and without results.
 * `categoryId` is the legacy category id on a category page and the product-type breadcrumb on a
 * taxonomy page, which is what the ad requests will send too.
 */
export const getMiraklViewEvent = (state: ReduxApplicationState): MiraklListingViewEvent | null => {
  const identity = getMiraklListingIdentity(state);
  const pageNumber = selectCurrentPage(state);

  if (!identity || state.products.error || !pageNumber) {
    return null;
  }

  const products = state.products.pages[pageNumber] ?? [];

  return {
    ...identity,
    pageNumber,
    // Organic products only: a sponsored product is reported by its impression, not the view.
    productIds: products
      .filter((product) => !product.sponsored)
      .slice(0, MIRAKL_VIEW_PRODUCT_LIMIT)
      .map(({ code }) => getProductIdFromProductCode(code)),
  };
};

/**
 * The ad units rendered on the page in the store, each listing only the ad ids of the sponsored
 * products actually shown, in display order. One `impression` is sent per unit. Empty when the
 * page holds no sponsored product, so nothing is sent.
 */
export const getMiraklAdUnits = (state: ReduxApplicationState): MiraklAdUnit[] => {
  const pageNumber = selectCurrentPage(state);
  const units = new Map<string, string[]>();

  for (const product of state.products.pages[pageNumber] ?? []) {
    if (product.sponsored) {
      const adIds = units.get(product.sponsored.adUnitId) ?? [];
      adIds.push(product.sponsored.adId);
      units.set(product.sponsored.adUnitId, adIds);
    }
  }

  return [...units].map(([adUnitId, adIds]) => ({ adIds, adUnitId }));
};
